import { ArrowRight, Move } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * RecentlyMoved — 404 recovery list of recent URL redirects.
 *
 * Design plan reference,
 * section 22.6 (404 page mockup). Rendered alongside the audience
 * picker and the "did you arrive from a HelpBubble?" callout.
 *
 * Each row shows the old path on the left, an arrow, then the new
 * path on the right rendered as a link. Paths are monospace so the
 * shape of the URL is legible at a glance ("/admin/apps/create" vs
 * "/admin/applications/create"). The list mirrors the
 * `inline_doc_aliases` table content (frontmatter `aliases:` entries)
 * — the Astro build collects every alias on rebuild and the docs
 * site's 404 page consumes the latest N entries.
 */

export interface RecentlyMovedEntry {
  /** The deprecated URL path. */
  from: string
  /** The new canonical URL path. */
  to: string
}

export interface RecentlyMovedProps extends React.HTMLAttributes<HTMLElement> {
  entries: RecentlyMovedEntry[]
  /** Override the header label. Defaults to 'Recently moved'. */
  label?: string
}

export const RecentlyMoved = React.forwardRef<HTMLElement, RecentlyMovedProps>(
  ({ entries, label = 'Recently moved', className, ...rest }, ref) => {
    if (entries.length === 0) return null

    return (
      <section
        ref={ref}
        aria-label={label}
        className={cn('flex flex-col gap-3', className)}
        {...rest}
      >
        <div className="flex items-center gap-2">
          <Move size={12} className="text-ia-text-tertiary" aria-hidden="true" />
          <span className="font-mono text-[9px] font-semibold uppercase tracking-[1.2px] text-ia-text-secondary">
            {label}
          </span>
        </div>
        <ul className="flex flex-col gap-1.5 m-0 p-0 list-none rounded-lg border border-ia-border bg-card p-3">
          {entries.map((entry) => (
            <li
              key={entry.from}
              className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-body-sm"
            >
              <code className="font-mono text-caption text-ia-text-secondary truncate">
                {entry.from}
              </code>
              <ArrowRight size={12} className="text-ia-text-tertiary" aria-hidden="true" />
              <a
                href={entry.to}
                className="font-mono text-caption text-ia-green-text hover:underline truncate"
              >
                {entry.to}
              </a>
            </li>
          ))}
        </ul>
      </section>
    )
  },
)
RecentlyMoved.displayName = 'RecentlyMoved'
